/**
 * Lädt das Baroon-AI-Widget-Script (erst nach Opt-in!) und verdrahtet
 * die Analytics-Listener auf die stabilen DOM-IDs des Widgets.
 *
 * data-api-base-url zeigt auf unseren Proxy (/api/chatbot) — dadurch
 * laufen /config, /chat und /lead über unsere Rate-Limits + Sanitizer,
 * und connect-src kann bei einer künftigen CSP 'self' bleiben. Nur das
 * Script selbst kommt vom Vendor-Origin (script-src-Eintrag nötig,
 * Audit FIX-M/FIND-011).
 *
 * Ändert der Vendor seine DOM-IDs, degradieren die Client-Events still —
 * die Server-Zähler (server_chat/server_lead) laufen weiter; die
 * Admin-Seite zeigt die Diskrepanz.
 */

import {
  trackChatbotEvent,
  trackFirstInteractionOnce,
} from './tracking';
import { CONSENT_VERSION } from './consent';

const SCRIPT_ID = 'baroon-chatbot-script';
const SCRIPT_SRC = 'https://www.chatbot.baroonai.de/widget/chatbot-widget.js';
const CUSTOMER_ID = 'Sizilianer-Dorsten';

export const WIDGET_IDS = {
  button: 'my-chatbot-button',
  input: 'my-chatbot-input',
  send: 'my-chatbot-send',
  leadSubmit: 'my-chatbot-lead-submit',
} as const;

let listenersAttached = false;

export function isChatbotScriptLoaded(): boolean {
  return Boolean(document.getElementById(SCRIPT_ID));
}

/** Wartet bis das Widget seinen Button gerendert hat (Adblocker/Netzfehler
 *  laufen in den Timeout → Launcher zeigt Fehlerzustand statt totem Button). */
function waitForButton(timeoutMs = 10_000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(WIDGET_IDS.button)) return resolve();
    const observer = new MutationObserver(() => {
      if (document.getElementById(WIDGET_IDS.button)) {
        observer.disconnect();
        clearTimeout(timer);
        resolve();
      }
    });
    const timer = setTimeout(() => {
      observer.disconnect();
      reject(new Error('chatbot widget did not render'));
    }, timeoutMs);
    observer.observe(document.body, { childList: true, subtree: true });
  });
}

/**
 * Zweiter Kanal für die Sitzungs-Zuordnung (0076).
 *
 * Hauptkanal ist der First-Party-Cookie `sz_chat`, den unser
 * Event-Endpoint setzt und den der Browser bei same-origin-Aufrufen
 * automatisch mitschickt — sowohl bei `fetch` als auch bei
 * `XMLHttpRequest`. Diese Absicherung greift nur, falls das Vendor-Script
 * `credentials: 'omit'` setzt: dann fehlt das Cookie, der Header kommt an.
 *
 * Läuft VOR der Script-Injektion. Patcht ausschließlich Aufrufe an unseren
 * eigenen Proxy-Pfad, alles andere bleibt unverändert.
 */
function installSessionHeaderFallback(): void {
  const w = window as typeof window & { __szChatFetchPatched?: boolean };
  if (w.__szChatFetchPatched) return;
  w.__szChatFetchPatched = true;

  const original = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    try {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.href
            : input.url;
      if (url.includes('/api/chatbot/')) {
        const sid = sessionStorage.getItem('sizilianer-chatbot-session');
        if (sid) {
          const headers = new Headers(init?.headers ?? {});
          headers.set('x-sizilianer-chat-session', `${sid}.${CONSENT_VERSION}`);
          return original(input, { ...init, headers });
        }
      }
    } catch {
      // Analytik darf den Chat niemals brechen.
    }
    return original(input, init);
  };
}

export function injectChatbotScript(): Promise<void> {
  installSessionHeaderFallback();
  if (!isChatbotScriptLoaded()) {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.dataset.customerId = CUSTOMER_ID;
    script.dataset.apiBaseUrl = `${window.location.origin}/api/chatbot`;
    document.body.appendChild(script);
  }
  return waitForButton();
}

/** Delegierte Listener (Capture-Phase, überleben Widget-Re-Renders). */
export function attachWidgetDomListeners(): void {
  if (listenersAttached) return;
  listenersAttached = true;

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target as Element | null;
      if (!target?.closest) return;
      if (target.closest(`#${WIDGET_IDS.button}`)) {
        // Capture-Phase läuft VOR dem Widget-Handler: ist das Fenster noch
        // zu, öffnet dieser Klick es (Toggle-Schließen zählt nicht).
        const win = document.getElementById('my-chatbot-window');
        const opens = !win || win.style.display !== 'flex';
        if (opens) {
          trackFirstInteractionOnce();
          trackChatbotEvent('widget_opened');
        }
      } else if (target.closest(`#${WIDGET_IDS.send}`)) {
        trackFirstInteractionOnce();
        trackChatbotEvent('message_sent');
      } else if (target.closest('.my-chatbot-quick-reply')) {
        trackFirstInteractionOnce();
        trackChatbotEvent('message_sent');
      } else if (target.closest(`#${WIDGET_IDS.leadSubmit}`)) {
        trackFirstInteractionOnce();
        trackChatbotEvent('lead_submitted');
      }
    },
    true,
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter') return;
      const target = event.target as Element | null;
      if (target?.id === WIDGET_IDS.input) {
        trackFirstInteractionOnce();
        trackChatbotEvent('message_sent');
      }
    },
    true,
  );
}

/** Beim SPA-Wechsel auf Nicht-Public-Routen das bereits geladene Widget
 *  ausblenden (Script bleibt — Reload entfernt es endgültig). */
export function setWidgetVisible(visible: boolean): void {
  const button = document.getElementById(WIDGET_IDS.button);
  const chatWindow = document.getElementById('my-chatbot-window');
  if (button) button.style.display = visible ? '' : 'none';
  if (chatWindow && !visible) chatWindow.style.display = 'none';
}

export function openChatWindow(): void {
  document.getElementById(WIDGET_IDS.button)?.click();
}
